import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants'

export default class MinecraftBlockBreakerAPNG {
  /** Internal reference to the picture HTML element */
  pictureElem

  /**
   * A helper to check the state of the reduced-motion page option
   */
  get reducedMotion() {
    return document.documentElement.classList.contains('reduced-motion')
  }

  /**
   * Convenience property for accessing the root element
   */
  get element() {
    return this.pictureElem
  }

  /**
   * Convenience property for accessing the source HTML element of the APNG asset
   */
  get animatedSourceElement() {
    return this.element.querySelector('source[type="image/apng"]')
  }

  constructor (path, alt, isAnimated) {
    return (async () => {
      this.animatedPath = path
      this.alt = alt || ''
      this.isAnimated = isAnimated || false
      this.emitter = GLOBAL_CONSTANTS.UTILS.EMITTER

      await this.createStaticSource()
      await this.createImage()

      return this
    })()
  }

  /**
   * Sets the path for the non-animated copy of the source image
   * @returns {Promise}
   */
  createStaticSource = async () => {
    this.staticPath = await MinecraftBlockBreakerAPNG.getFirstFrameImage(this.animatedPath)
  }

  /**
   * Creates the markup for the image
   * @returns {Promise}
   */
  createImage = async () => {
    this.pictureElem = document.createElement('picture')

    if (this.isAnimated) {
      const sourceApngElem = document.createElement('source')
      const sourcePngElem = document.createElement('source')

      sourceApngElem.type = 'image/apng'
      sourcePngElem.srcset = ''

      sourcePngElem.type = 'image/png'
      sourcePngElem.srcset = this.staticPath

      this.pictureElem.appendChild(sourceApngElem)
      this.pictureElem.appendChild(sourcePngElem)
    } else {
      this.staticPath = this.animatedPath

      const sourceElem = document.createElement('source')
      sourceElem.type = await MinecraftBlockBreakerAPNG.getMimeType(this.animatedPath)
      sourceElem.srcset = this.staticPath

      this.pictureElem.appendChild(sourceElem)
    }

    this.setMotionState()

    const imgElem = document.createElement('img')
    imgElem.alt = this.alt
    imgElem.src = this.staticPath
    this.pictureElem.appendChild(imgElem)
  }

  /**
   * Toggles animation based on the state of reducedMotion
   */
  setMotionState = () => {
    if (!this.animatedSourceElement) return

    this.animatedSourceElement.srcset = this.reducedMotion ? '' : this.animatedPath
  }

  boundSetMotionState = this.setMotionState.bind(this)

  addMotionListener = () => {
    if (this.isAnimated) {
      this.emitter.on(GLOBAL_CONSTANTS.EVENTS.REDUCED_MOTION_CHANGE, this.boundSetMotionState)
    }
  }

  removeMotionListener = () => {
    this.emitter.off(GLOBAL_CONSTANTS.EVENTS.REDUCED_MOTION_CHANGE, this.boundSetMotionState)
  }

  /**
   * Forces the static asset regardless of reducedMotion option
   */
  setStatic = () => {
    this.animatedSourceElement.srcset = ''
  }

  /**
   * Gets the MIME type for use in an img's type attribute
   */
  static getMimeType = async (path) => {
    const reponse = await fetch(path, { method: 'HEAD' })

    return reponse.headers.get('Content-Type')
  }

  /**
   * Creates a data URL of the first frame of the animated image
   */
  static getFirstFrameImage = (animatedSource) => {
    return new Promise((resolve, reject) => {
      const img = new Image()

      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        const context = canvas.getContext('2d')
        context.drawImage(img, 0, 0)

        const firstFrameDataURL = canvas.toDataURL('image/png')
        resolve(firstFrameDataURL)
      }
      img.onerror = reject
      img.src = animatedSource
    })
  }
}
